import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SingleNoteComponent from './singleNote';
import MessageBox from '../../util/messageBox';
import AddIcon from '@mui/icons-material/Add';
import _ from 'lodash';
import moment from 'moment';
import API from '../../app/api';
import {
    clearNoteFilters,
    getNotesByNoteFilters,
    getOptionsFromClinicalNotes,
    hideNoteForPatient,
    loading,
    loadNotesForPatient,
    noteFilters,
    patientsNotes,
    removeNotes,
    setNoteFilters,
    setNoteType,
} from '../../store/reducers/notes';
import { hasPermission, Permissions } from '../../store/reducers/user';
import Grid from '@mui/material/Grid';
import { getECSById } from '../../pages/ward/forms/ecs/ecs';
import IconButton from '@mui/material/IconButton';
import InformationBox from '../other/informationBox';
import { agylePref } from '../../store/reducers/preferences';
import AgyleButton from '../ui/buttons/agyleButton';
import AgyleTextField from '../ui/fields/agyleTextField';
import SearchIcon from '@mui/icons-material/Search';
import { AgyleNamedIcon } from '../icons/agyle';
import useOpenState from '../../hooks/useOpenState';
import clsx from 'clsx';
import MarkAsErrorNote from './component/markAsErrorNote';
import NewBodyMapNote from './component/newBodyMapNote';
import ClinicalNoteFilterModal from './component/clinicalNoteFilterModal';
import HistoryClinicalNotes from './component/historyClinicalNotes';
import NewNoteDraggableModal from './component/newNoteDraggableModal';
import { getConfig } from '../../util/configUtil';
import AgyleLoadingSpinner from '../other/agyleLoadingSpinner';
import AdmissionClinicalNotes from './admissionClinicalNotes';
import AgyleToggleButtons from '../ui/buttons/agyleToggleButtons';
import ActivitySpellSelector from './component/activitySpellSelector';
import { ctxPatientLocked } from '../../store/reducers/patientContext';
import { useEventListener } from '../../hooks/useEventListener';
import { useClinicalNoteStyles } from './useClinicalNoteStyles';

function ClinicalNotesComponent(props) {
    const dispatch = useDispatch();
    const classes = useClinicalNoteStyles();
    const { t } = useTranslation();
    const controller = new AbortController();

    // Selectors
    const filter = useSelector(noteFilters);
    const agylePreference = useSelector(agylePref);
    const notesObjectOriginal = useSelector(patientsNotes(props.patient?.patient?.__id || null));
    const isLoading = useSelector(loading);
    const isLocked = useSelector(ctxPatientLocked);

    // Config
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);
    const enableClinicalNotesFilters = getConfig('enableClinicalNotesFilters', false);
    const showAllNotes = !props.pastAttendances && showAllAttendance;

    // Open states
    const [isBodyMapOpen, openBodyMap, closeBodyMap] = useOpenState(false, true);
    const [isAuditOpen, openAudit, closeAudit] = useOpenState(false, true);
    const [isEditNoteOpen, openEditNote, closeEditNote] = useOpenState(false, true);
    const [isMarAsErrorOpen, openMarAsError, closeMarAsError] = useOpenState(false, true);
    const [isAckOpen, openAck, closeAck] = useOpenState(false, true);
    const [isHideConfirmOpen, openHideConfirm, closeHideConfirm] = useOpenState(false, true);
    const [isFilterOpen, openFilter, closeFilter] = useOpenState(false, true);

    // Cancel note with reason
    const noteStore = React.useRef(null);

    // Filter options
    const [filterOptions, setFilterOptions] = useState({
        users: [],
        spec: [],
        type: [],
        recType: [],
    });
    const [filterApplied, setFilterApplied] = useState(false);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [filterText, setFilterText] = useState('');

    // Note state
    const [noteActive, setNoteActive] = useState(null);
    const [defaultNoteType, setDefaultNoteType] = useState(agylePreference.defaultNoteType);
    const [noteChanged, setNoteChanged] = useState(false);
    const [notesObject, setNotesObject] = useState([]);
    const [noteHistoryObject, setNoteHistoryObject] = useState(null);

    // Others
    const [clinicNotesWindowSize, setClinicNotesWindowSize] = useState({ width: 1, height: 1 });
    const canAddNotes = () => dispatch(hasPermission(Permissions.AGYLE_CAN_ADD_CLINICAL_NOTES)) || !props.disableSave;
    const clinicNotesWindowRef = useRef(null);

    const [warningHeight, setWarningHeight] = useState(0);
    const [headerToHeight, setHeaderToHeight] = useState(false);

    useEffect(() => {
        dispatch(setNoteType(props.disableSave ? 'historical' : 'clinical'));
    }, [props.disableSave]);

    useEffect(() => {
        setClinicNotesWindowSize({
            width: clinicNotesWindowRef.current.offsetWidth,
            height: clinicNotesWindowRef.current.offsetHeight,
        });
        return () => {
            dispatch(clearNoteFilters());
        };
    }, []);

    useEffect(() => {
        setFilteredNotes(dispatch(getNotesByNoteFilters(notesObject, showAllNotes)));
    }, [filter, notesObject]);

    useEffect(() => {
        if (isEditNoteOpen && isFilterOpen) {
            closeFilter();
        }
    }, [isEditNoteOpen]);

    useEffect(() => {
        if (notesObjectOriginal && !_.isEmpty(notesObjectOriginal)) {
            if (enableClinicalNotesFilters) {
                const { users, spec, recType, noteType } = dispatch(getOptionsFromClinicalNotes(notesObjectOriginal, showAllNotes));
                const optObj = {
                    users: _.isEmpty(users) ? [] : _.sortBy(Array.from(users)),
                    spec: _.isEmpty(spec) ? [] : _.sortBy(Array.from(spec)),
                    type: _.isEmpty(noteType) ? [] : _.sortBy(Array.from(noteType.keys())),
                    recType: _.isEmpty(recType) ? [] : _.sortBy(Array.from(recType)),
                };
                setFilterOptions(optObj);
            }
            setNotesObject(notesObjectOriginal);
        }
    }, [notesObjectOriginal, filter]);

    useEffect(() => {
        if (props.pastAttendances && notesObject && noteChanged) {
            getECSById(props.patient).then((res) => {});
        }
    }, [notesObject]);

    useEffect(() => {
        if (isFilterOpen && isEditNoteOpen) {
            closeEditNote();
        }
    }, [isFilterOpen]);

    const reloadNotes = () => {
        dispatch(loadNotesForPatient(props.patient, controller.signal, !showAllNotes));
    };

    useEffect(() => {
        reloadNotes();

        return () => {
            controller.abort();
            dispatch(removeNotes());
        };
    }, []);

    useEffect(() => {
        const filterCheck =
            filter.filterNoteType !== 'all' ||
            filter.status !== 'all' ||
            !_.isEmpty(filter.type) ||
            !_.isEmpty(filter.user) ||
            !_.isEmpty(filter.spec) ||
            filter.showFirst !== 'all' ||
            filter.showMyNotes ||
            filter.activitySelectedNotes ||
            !_.isEmpty(filterText);
        setFilterApplied(filterCheck);
    }, [filter]);

    useEffect(() => {
        let height = 0;
        if (filterApplied) {
            height = document.getElementById('note-warning')?.offsetHeight || 0;
        }
        setWarningHeight(height);
    }, [filterApplied]);

    const resize = () => {
        const header = document.getElementById('agyle-header')?.offsetHeight || 0;
        setHeaderToHeight(header > 62);
    };

    useEventListener('resize', resize);

    const addNote = () => {
        if (defaultNoteType) {
            dispatch(setNoteType(defaultNoteType));
        }
        setNoteActive(null);
        openEditNote();
    };

    const addBodyMap = () => {
        setNoteActive(null);
        openBodyMap();
    };

    const confirmHide = () => {
        dispatch(
            hideNoteForPatient(props.patient.patient.__id, noteActive, () => {
                dispatch(removeNotes());
                setNoteChanged(true);
                reloadNotes();
                closeHideConfirm();
            }),
        );
    };

    const hideNote = (note) => () => {
        setNoteActive(note);
        openHideConfirm();
    };

    const editNote = (note) => () => {
        setNoteActive(note);
        openEditNote();
    };

    const isVisible = (note) => {
        if (!filterText) return true;
        if (note?.note) {
            if (note.note.startsWith('data:image/png;base64')) return false;
            return note.note.toLowerCase().includes(filterText.toLowerCase());
        }
        return false;
    };

    const handleFilterChange = (event) => {
        if (!event.target.value && event.target.value.length === 0) {
            setFilterText(null);
            handleSetFilerField('expanded', false);
        } else {
            setFilterText(event.target.value);
            handleSetFilerField('expanded', true);
        }
    };

    const showNoteAudit = (note) => {
        return () => {
            API.getNoteAudit(props.patient.patient.__id, note.__id, controller.signal).then((audit) => {
                if (audit?.__data && audit.__data.length > 0) {
                    let noteHistory = audit.__data;
                    noteHistory = _.sortBy(noteHistory, [
                        function (note) {
                            return moment(note.lastModifiedDate).toDate();
                        },
                    ]);
                    noteHistory = _.reverse(noteHistory);
                    setNoteHistoryObject(noteHistory);
                    openAudit();
                } else {
                    openAck();
                }
            });
        };
    };

    const checkLastOfType = (note) => {
        if (note?.noteType) {
            // Filter and sort by created date
            const notes = _.orderBy(
                _.filter(notesObject, (entry) => {
                    return entry.noteType === note?.noteType && entry.canceled !== true;
                }),
                ['createdDate'],
                ['desc'],
            );

            return _.isEqual(notes[0], note);
        }
        return false;
    };

    const markAsError = (note) => {
        noteStore.current = note;
        openMarAsError();
    };

    const handleToggleFilter = () => (isFilterOpen ? closeFilter() : openFilter());

    const handleCloseMarkAsError = () => {
        closeMarAsError();
        noteStore.current = null;
        setNoteChanged(true);
    };

    const handleCloseBodyMapEditor = (noteChange = false) => {
        closeBodyMap();
        setNoteChanged(noteChange);
    };

    const handleCloseNoteEditor = (noteChange = false) => {
        closeEditNote();
        setNoteChanged(noteChange);
    };

    const renderNotes = () => {
        if (showAllNotes) {
            return filteredNotes.map((noteAdmission, key) => (
                <Fragment key={noteAdmission?.admission?.__id || `${Math.random()}_${key}`}>
                    <AdmissionClinicalNotes
                        filterText={filterText}
                        noteAdmission={noteAdmission}
                        hideNote={hideNote}
                        editNote={editNote}
                        showNoteAudit={showNoteAudit}
                        checkLastOfType={checkLastOfType}
                        markAsError={markAsError}
                        disableSave={props.disableSave}
                        patient={props.patient}
                    />
                </Fragment>
            ));
        } else {
            return (
                notesObject &&
                filteredNotes.map(
                    (note, index) =>
                        isVisible(note) && (
                            <SingleNoteComponent
                                filterText={filterText}
                                index={index}
                                onHide={hideNote(note)}
                                onEdit={editNote(note)}
                                onAudit={showNoteAudit(note)}
                                isLastOfType={checkLastOfType(note)}
                                handleError={() => markAsError(note)}
                                key={note.__id}
                                note={note}
                                disableSave={props.disableSave}
                                patient={props.patient}
                            />
                        ),
                )
            );
        }
    };

    const handleSetFilerField = (name, value) => {
        dispatch(setNoteFilters({ [name]: value }));
    };

    return (
        <div style={{ height: headerToHeight ? '96%' : '99%' }}>
            {filterApplied && (
                <InformationBox type="warning" id="note-warning">
                    {t('Caution! Filters Applied - Not all results are shown.')}
                </InformationBox>
            )}

            <Grid container alignItems="center" className={classes.header}>
                <Grid
                    item
                    className={classes.searchBar}
                    flexGrow={{
                        xs: 0,
                        sm: 1,
                    }}
                >
                    <AgyleTextField
                        inputPropsClasses={classes.searchText}
                        size="small"
                        fullWidth
                        placeholder={t('Search notes')}
                        variant="contained"
                        onChange={handleFilterChange}
                        value={filterText}
                        noBorder={false}
                        startAdornment={<SearchIcon style={{ marginRight: 8 }} />}
                        shrink
                        smallerPlaceholder
                    />
                    {showAllNotes && (
                        <>
                            <div style={{ width: 100 }}>
                                <AgyleToggleButtons
                                    classNameRoot={clsx(classes.toggleRoot, 'header')}
                                    classNameLabel={classes.toggleLabel}
                                    classNameSelected={classes.toggleSelected}
                                    onChange={(e) => {
                                        if (_.isBoolean(e)) {
                                            handleSetFilerField('showMyNotes', e);
                                        }
                                    }}
                                    value={filter.showMyNotes}
                                    size="small"
                                    options={[
                                        { value: false, label: 'All' },
                                        { value: true, label: 'My' },
                                    ]}
                                    fullWidth
                                />
                            </div>

                            <div style={{ width: 100 }}>
                                <AgyleToggleButtons
                                    classNameRoot={clsx(classes.toggleRoot, 'header')}
                                    classNameLabel={clsx(classes.toggleLabel, 'icon')}
                                    classNameSelected={classes.toggleSelected}
                                    onChange={(val) => {
                                        if (_.isBoolean(val)) {
                                            handleSetFilerField('expanded', val);
                                        }
                                    }}
                                    value={filter.expanded}
                                    size="small"
                                    options={[
                                        {
                                            value: false,
                                            label: <AgyleNamedIcon className={classes.icon} name="collapseAll" />,
                                        },
                                        {
                                            value: true,
                                            label: <AgyleNamedIcon className={classes.icon} name="expandAll" />,
                                        },
                                    ]}
                                    fullWidth
                                />
                            </div>

                            <div style={{ minWidth: 260 }}>
                                <ActivitySpellSelector type="note" admissions={notesObjectOriginal?.map((n) => n.admission)} />
                            </div>
                        </>
                    )}

                    {enableClinicalNotesFilters && (
                        <IconButton size="small" onClick={handleToggleFilter} className={classes.filterButton}>
                            <AgyleNamedIcon name="filters" withTooltip fontSize="10px" />
                        </IconButton>
                    )}
                </Grid>

                <Grid item className={classes.buttonsHeader}>
                    <AgyleButton
                        color="primary"
                        variant="contained"
                        onClick={addBodyMap}
                        className={classes.buttonHeader}
                        startIcon={<AddIcon />}
                        disabled={!canAddNotes() || isLocked}
                    >
                        {t('Add Body Map')}
                    </AgyleButton>

                    <AgyleButton
                        color="primary"
                        variant="contained"
                        onClick={addNote}
                        className={classes.buttonHeader}
                        startIcon={<AddIcon />}
                        disabled={!canAddNotes() || isLocked}
                    >
                        {t('Add New Note')}
                    </AgyleButton>
                </Grid>
            </Grid>

            <NewNoteDraggableModal
                open={isEditNoteOpen}
                onClose={handleCloseNoteEditor}
                patient={props.patient}
                patientType={props.patientType}
                disableSave={props.disableSave}
                clinicNotesWindowSize={clinicNotesWindowSize}
                setDefaultNoteType={setDefaultNoteType}
                noteActive={noteActive}
                showAllNotes={showAllNotes}
            />

            <ClinicalNoteFilterModal
                open={isFilterOpen}
                onClose={closeFilter}
                notesObject={notesObject}
                clinicNotesWindowRef={clinicNotesWindowRef}
                filterOptions={filterOptions}
                pastAttendances={props.pastAttendances}
            />

            <NewBodyMapNote
                open={isBodyMapOpen}
                onClose={handleCloseBodyMapEditor}
                patient={props.patient}
                setDefaultNoteType={setDefaultNoteType}
                showAllNotes={showAllNotes}
            />

            <MarkAsErrorNote open={isMarAsErrorOpen} onClose={handleCloseMarkAsError} patient={props.patient} note={noteStore.current} />

            <HistoryClinicalNotes open={isAuditOpen} onClose={closeAudit} filterText={filterText} historyNotes={noteHistoryObject} />

            <div
                className={clsx(classes.notesContent, 'scroll-normal-white')}
                style={{ height: `calc(100% - 60px - ${warningHeight}px)` }}
                ref={clinicNotesWindowRef}
            >
                {!isLoading && renderNotes()}
                {isLoading && <AgyleLoadingSpinner label={t('Loading clinical notes. Please wait.')} />}
            </div>

            <MessageBox
                onClose={closeHideConfirm}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={confirmHide}
                message={t('Are you sure you want mark this note as incorrect? To revert it back you will need to contact administrator!')}
                open={isHideConfirmOpen}
            />

            <MessageBox
                onClose={closeAck}
                title={t('Information')}
                message={t('This note do not have any previous entries')}
                open={isAckOpen}
            />
        </div>
    );
}

export default ClinicalNotesComponent;
